import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { submitFeedback, submitContact } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Bug, Lightbulb, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3.5 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none";

export default function Feedback() {
  const { pick } = useLang();
  const { heading } = useSettings();
  const [params] = useSearchParams();
  const qp = params.get("type");
  const initType = qp === "suggestion" ? "suggestion" : qp === "bug" ? "bug" : "general";
  const [type, setType] = useState(initType);
  const [form, setForm] = useState({ name: "", subject: "", area: params.get("area") || "", message: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const h = heading("feedback.hero", { title_deva: "संपर्क एवं प्रतिक्रिया", title_en: "Contact & Feedback", overline: "We're listening", sub_en: "Reach the Dharmartha team, report a bug, or suggest content — all in one place." });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if ((form.message || "").trim().length < 5) { toast.error(pick("कृपया थोड़ा विवरण दें।", "Please add a little more detail.")); return; }
    if (type === "general" && (!form.name || !form.email)) { toast.error(pick("कृपया नाम व ईमेल भरें।", "Please add your name and email.")); return; }
    setSending(true);
    try {
      if (type === "general") {
        await submitContact({ name: form.name, email: form.email, subject: form.subject, message: form.message });
      } else {
        await submitFeedback({ area: form.area, message: form.message, email: form.email, type, page_url: window.location.href });
      }
      setDone(true);
      toast.success(pick("धन्यवाद! आपका संदेश मिल गया।", "Thank you — your message has been received."));
    } catch (err) {
      toast.error(pick("भेजने में समस्या हुई।", "Could not send"), { description: err.response?.data?.detail || err.message });
    } finally { setSending(false); }
  };

  const TypeBtn = ({ value, icon: Icon, label }) => (
    <button type="button" data-testid={`feedback-type-${value}`} onClick={() => setType(value)}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${type === value ? "border-saffron bg-[var(--saffron)]/10 text-saffron" : "border-line text-ink-2 hover:border-[var(--ink)]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <>
      <Seo path="/feedback" title="Contact & Feedback — Dharmartha" description="Contact the Dharmartha team, report a bug, or suggest content." />
      <PageHero
        overline={pick(h.overline || "हमें बताएँ", h.overline || "We're listening")}
        deva={h.title_deva || "संपर्क एवं प्रतिक्रिया"}
        en={pick(h.title_deva || "संपर्क एवं प्रतिक्रिया", h.title_en || "Contact & Feedback")}
        tagline={pick(h.sub_deva || "संपर्क करें, कोई त्रुटि बताएँ, या सामग्री का सुझाव दें — सब एक ही स्थान पर।", h.sub_en || "Reach the Dharmartha team, report a bug, or suggest content — all in one place.")}
      />
      <section className="mx-auto max-w-2xl px-5 py-12 md:px-8 md:py-16" data-testid="feedback-page">
        {done ? (
          <div className="flex flex-col items-center gap-4 border border-line bg-surface p-10 text-center" data-testid="feedback-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600" />
            <h2 className="font-serif-latin text-2xl text-ink">{pick("धन्यवाद 🙏", "Thank you 🙏")}</h2>
            <p className="max-w-md text-sm text-ink-2">{pick("आपका संदेश हमारी टीम तक पहुँच गया है। हम इसकी समीक्षा करेंगे।", "Your message has reached our team. We'll review it carefully.")}</p>
            <button data-testid="feedback-another" onClick={() => { setDone(false); setForm({ name: "", subject: "", area: "", message: "", email: "" }); }} className="mt-2 border border-line px-5 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
              {pick("एक और भेजें", "Send another")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-line bg-surface p-6 md:p-8" data-testid="feedback-form">
            <label className="mb-2 block text-xs uppercase tracking-wider text-ink-3">{pick("यह किस बारे में है?", "What is this about?")}</label>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row">
              <TypeBtn value="general" icon={Mail} label={pick("संपर्क / सामान्य", "Contact / General")} />
              <TypeBtn value="bug" icon={Bug} label={pick("त्रुटि / समस्या", "Bug / Issue")} />
              <TypeBtn value="suggestion" icon={Lightbulb} label={pick("सामग्री सुझाव", "Suggestion")} />
            </div>

            {type === "general" && (
              <div className="mb-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-ink-3">{pick("नाम", "Name")} <span className="text-saffron">*</span></label>
                  <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inp} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-ink-3">{pick("विषय", "Subject")}</label>
                  <input data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={inp} />
                </div>
              </div>
            )}

            {type !== "general" && (
              <div className="mb-4">
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-ink-3">{pick("कौन-सा पृष्ठ / अनुभाग", "Page or section (optional)")}</label>
                <input data-testid="feedback-area" value={form.area} onChange={set("area")}
                  placeholder={pick("जैसे पंचांग, मंत्र, मंदिर…", "e.g. Panchang, a mantra, Temples…")} className={inp} />
              </div>
            )}

            <div className="mb-4">
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-ink-3">
                {type === "bug" ? pick("क्या हुआ?", "What went wrong?") : type === "suggestion" ? pick("आपका सुझाव", "Your suggestion") : pick("आपका संदेश", "Your message")} <span className="text-saffron">*</span>
              </label>
              <textarea data-testid="feedback-message" required rows={5} value={form.message} onChange={set("message")}
                placeholder={type === "bug" ? pick("समस्या का विवरण दें…", "Describe the problem and where you saw it…") : type === "suggestion" ? pick("आप क्या जोड़ना/सुधारना चाहेंगे?", "What would you like us to add or improve?") : pick("आप हमसे क्या कहना चाहेंगे?", "How can we help?")} className={inp} />
            </div>

            <div className="mb-6">
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-ink-3">{pick("ईमेल", "Your email")} {type === "general" ? <span className="text-saffron">*</span> : pick("(वैकल्पिक)", "(optional)")}</label>
              <input data-testid="feedback-email" type="email" value={form.email} onChange={set("email")}
                placeholder={type === "general" ? pick("आपका ईमेल पता", "Your email address") : pick("यदि आप उत्तर चाहते हैं", "If you'd like a reply")} className={inp} />
            </div>

            <button type="submit" data-testid="feedback-submit" disabled={sending}
              className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {pick("भेजें", "Submit")}
            </button>
          </form>
        )}
      </section>
    </>
  );
}
